import type { CmmdUserForgeAutomationRule } from "@cmmd-forge/contracts";
import { Link, useNavigate } from "@tanstack/react-router";

import { Button } from "~/components/ui/button";
import { useFeatureFlag } from "~/hooks/useFeatureFlag";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { AutomationFormLayout } from "./AutomationFormLayout";
import { AutomationLoadFailure } from "./AutomationLoadFailure";
import {
  cronFromDraft,
  draftFromCloudRule,
  draftFromLocalAutomation,
  scheduleDescriptionFromDraft,
  type AutomationDraft,
  type AutomationProjectOption,
} from "./automationFormModel";
import type { LocalAutomation } from "./localAutomationClient";
import { useForgeAutomationsIndex } from "./useForgeAutomationsIndex";
import {
  automationResponseMatchesSelection,
  validateAutomationModelSelection,
} from "./useAutomationProviderSelection";

type AutomationsIndexState = ReturnType<typeof useForgeAutomationsIndex>;

type EditTarget =
  | { readonly kind: "cloud"; readonly rule: CmmdUserForgeAutomationRule }
  | { readonly automation: LocalAutomation; readonly kind: "local" }
  | { readonly kind: "missing" };

interface EditSessionProps {
  readonly automationId: string;
  readonly everyMinuteAvailable: boolean;
  readonly projects: readonly AutomationProjectOption[];
  readonly state: AutomationsIndexState;
}

const EXISTING_LOCAL_PROJECT_ID = "__existing_local_project__";

function automationLoadError(automationId: string, state: AutomationsIndexState): string | null {
  if (automationId.startsWith("cloud-")) return state.cloudAutomationsLoadError;
  if (automationId.startsWith("local-")) return state.localAutomationsLoadError;
  return null;
}

function resolveEditTarget(automationId: string, state: AutomationsIndexState): EditTarget {
  if (automationId.startsWith("cloud-")) {
    const id = Number(automationId.slice(6));
    const rule = state.cloudAutomations.find((candidate) => candidate.id === id);
    return rule ? { kind: "cloud", rule } : { kind: "missing" };
  }
  if (automationId.startsWith("local-")) {
    const id = automationId.slice(6);
    const automation = state.localAutomations.find((candidate) => candidate.id === id);
    return automation ? { automation, kind: "local" } : { kind: "missing" };
  }
  return { kind: "missing" };
}

function AutomationNotFound() {
  return (
    <main className="grid min-h-0 flex-1 place-items-center px-4 text-center">
      <div>
        <h1 className="text-lg font-semibold">Automation not found</h1>
        <Button
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          render={<Link to="/automations" />}
          variant="ghost"
        >
          Back to automations
        </Button>
      </div>
    </main>
  );
}

interface CloudEditSessionProps extends EditSessionProps {
  readonly rule: CmmdUserForgeAutomationRule;
}

async function reopenPersistedAutomation(input: {
  readonly automationId: string;
  readonly navigate: ReturnType<typeof useNavigate>;
}) {
  try {
    await input.navigate({
      to: "/automations/$automationId",
      params: { automationId: input.automationId },
    });
    return null;
  } catch {
    return {
      reconciliationRequired: true as const,
      recoveryAutomationId: input.automationId,
      error: `Changes to ${input.automationId} were saved, but Forge could not reopen it. Return to Automations and open that exact record.`,
    };
  }
}

function CloudEditSession({
  automationId,
  everyMinuteAvailable,
  projects,
  rule,
  state,
}: CloudEditSessionProps) {
  const navigate = useNavigate();

  async function save(draft: AutomationDraft) {
    if (!(await validateAutomationModelSelection(draft))) {
      return "That provider or model is no longer available for this project. Choose an available model and try again.";
    }
    const updated = await state.updateCloudAutomation(rule.id, {
      name: draft.name.trim(),
      ...(draft.modelSelection
        ? {
            modelSelection: {
              provider: draft.modelSelection.provider,
              model: draft.modelSelection.model,
            },
          }
        : {}),
      prompt: draft.prompt.trim(),
      cronExpression: cronFromDraft(draft),
      scheduleDescription: scheduleDescriptionFromDraft(draft),
      timezone: draft.timezone,
      requirePrReview: draft.requirePrReview,
    });
    if (!updated || !draft.modelSelection) return null;
    const confirmed = automationResponseMatchesSelection({
      expectedId: rule.id,
      expectedProjectId: rule.projectId,
      response: updated,
      selection: draft.modelSelection,
    })
      ? updated
      : await state.reconcileCloudAutomation(rule.id);
    if (
      automationResponseMatchesSelection({
        expectedId: rule.id,
        expectedProjectId: rule.projectId,
        response: confirmed ?? null,
        selection: draft.modelSelection,
      })
    ) {
      return reopenPersistedAutomation({ automationId, navigate });
    }
    return {
      reconciliationRequired: true as const,
      recoveryAutomationId: automationId,
      error:
        "The change may have been saved, but its provider and model could not be confirmed. Return to Automations and refresh before editing again.",
    };
  }

  return (
    <AutomationFormLayout
      busy={state.updatingAutomationKey === `cloud:${rule.id}`}
      error={state.error}
      everyMinuteAvailable={everyMinuteAvailable}
      initialDraft={draftFromCloudRule(rule)}
      localExecutionAvailable={state.localExecutionAvailable}
      mode="edit"
      projects={projects}
      targetLocked
      onSubmit={save}
    />
  );
}

interface LocalEditSessionProps extends EditSessionProps {
  readonly automation: LocalAutomation;
}

function LocalEditSession({
  automation,
  automationId,
  everyMinuteAvailable,
  projects,
  state,
}: LocalEditSessionProps) {
  const navigate = useNavigate();
  const matchingProject = projects.find((project) => project.name === automation.projectName);
  const projectId = matchingProject
    ? matchingProject.id
    : automation.projectName
      ? EXISTING_LOCAL_PROJECT_ID
      : "";
  const editableProjects =
    automation.projectName && !matchingProject
      ? [
          { id: EXISTING_LOCAL_PROJECT_ID, name: automation.projectName, githubRepo: null },
          ...projects,
        ]
      : projects;

  async function save(draft: AutomationDraft) {
    if (draft.scheduleKind === "custom") {
      return "Choose a supported schedule before saving this automation.";
    }
    const project = projects.find((option) => option.id === draft.selectedProjectId);
    const projectName =
      draft.selectedProjectId === EXISTING_LOCAL_PROJECT_ID
        ? automation.projectName
        : (project?.name ?? null);
    const hasClockTime = draft.scheduleKind === "daily" || draft.scheduleKind === "weekly";
    const updated = await state.updateLocalAutomation({
      id: automation.id,
      name: draft.name.trim(),
      prompt: draft.prompt.trim(),
      scheduleKind: draft.scheduleKind,
      scheduleTime: hasClockTime ? draft.scheduleTime : null,
      scheduleDayOfWeek: draft.scheduleKind === "weekly" ? draft.scheduleDayOfWeek : null,
      timezone: draft.timezone,
      projectName,
    });
    if (!updated) return null;
    return reopenPersistedAutomation({ automationId, navigate });
  }

  return (
    <AutomationFormLayout
      busy={state.updatingAutomationKey === `local:${automation.id}`}
      error={state.error}
      everyMinuteAvailable={everyMinuteAvailable}
      initialDraft={{ ...draftFromLocalAutomation(automation), selectedProjectId: projectId }}
      localExecutionAvailable={state.localExecutionAvailable}
      mode="edit"
      projects={editableProjects}
      targetLocked={false}
      onSubmit={save}
    />
  );
}

function EditSession({ automationId, everyMinuteAvailable, projects, state }: EditSessionProps) {
  const target = resolveEditTarget(automationId, state);
  if (target.kind === "missing") return <AutomationNotFound />;
  return target.kind === "cloud" ? (
    <CloudEditSession
      key={`cloud:${target.rule.updatedAt}`}
      automationId={automationId}
      everyMinuteAvailable={everyMinuteAvailable}
      projects={projects}
      rule={target.rule}
      state={state}
    />
  ) : (
    <LocalEditSession
      key={`local:${target.automation.updatedAt}`}
      automation={target.automation}
      automationId={automationId}
      everyMinuteAvailable={everyMinuteAvailable}
      projects={projects}
      state={state}
    />
  );
}

export function ForgeAutomationEditPage({ automationId }: { readonly automationId: string }) {
  const state = useForgeAutomationsIndex();
  const everyMinuteAvailable = useFeatureFlag("cmmd_automations_every_minute");
  const projects: readonly AutomationProjectOption[] = state.projects.map((project) => ({
    id: String(project.id),
    name: project.title,
    githubRepo:
      project.githubRepoUrl?.match(/github\.com[/:]([^/]+\/[^/]+?)(?:\.git)?\/?$/i)?.[1] ?? null,
  }));
  if (state.loading) return <main className="min-h-0 flex-1 bg-background" />;
  const loadError = automationLoadError(automationId, state);
  if (loadError) return <AutomationLoadFailure error={loadError} onRetry={state.refresh} />;
  return (
    <EditSession
      automationId={automationId}
      everyMinuteAvailable={everyMinuteAvailable}
      projects={projects}
      state={state}
    />
  );
}
